/**
 * Early-access consent flow configuration.
 *
 * Centralizes which deployment + which vault handle the consent flow
 * (Welcome / Consent / Done) targets. The deployment id is a CUE registry key —
 * see packages/sdk/src/ui/deployments/generated/index.ts.
 *
 * The vault ref is NOT hardcoded: it is derived from the generated registry by
 * picking the deployment's active + enabled LP vault — the same
 * registry-driven selection the dashboard's VaultPage uses (VAULT_CONFIGS is
 * sorted active+enabled first, and VaultPage defaults to VAULT_CONFIGS[0]).
 * Rotating the live vault in CUE (e.g. beta5 → beta6 → beta7) therefore
 * updates the dashboard and this consent flow together, with no edit here.
 */

import { getDeployment } from "@thema/sdk/ui";

export const CONSENT_DEPLOYMENT_ID = "thema/thema-dev/tbtc01";

function deriveConsentVaultRef(): string {
  const deployment = getDeployment(CONSENT_DEPLOYMENT_ID);
  if (!deployment) {
    throw new Error(
      `Consent deployment "${CONSENT_DEPLOYMENT_ID}" is missing from the generated registry.`,
    );
  }
  const chainDep = deployment.chainDeployments[deployment.defaultChainId];
  if (!chainDep) {
    throw new Error(
      `Consent deployment "${CONSENT_DEPLOYMENT_ID}" has no chain deployment for its default chain "${deployment.defaultChainId}".`,
    );
  }

  // Live LP vaults only: deployed address, enabled, lifecycle 'active'
  // (lifecycle defaults to 'active' for older registry entries that omit it).
  const candidates: { handle: string; label: string }[] = [];
  for (const omt of Object.values(chainDep.omts ?? {})) {
    for (const [handle, vault] of Object.entries(omt.vaults ?? {})) {
      if (vault.kind !== "lp") continue;
      if (!vault.address) continue;
      if (vault.enabled === false) continue;
      if ((vault.lifecycle ?? "active") !== "active") continue;
      candidates.push({ handle, label: vault.label ?? vault.name ?? handle });
    }
  }

  if (candidates.length === 0) {
    throw new Error(
      `No active LP vault found for consent deployment "${CONSENT_DEPLOYMENT_ID}" — the early-access flow has no vault to target.`,
    );
  }

  // Deterministic tie-break mirroring VAULT_CONFIGS' ordering (label sort
  // within the same enabled/lifecycle/chain bucket).
  candidates.sort((a, b) => a.label.localeCompare(b.label));
  return candidates[0].handle;
}

export const CONSENT_VAULT_REF = deriveConsentVaultRef();
